import { DateTime } from 'luxon';

import { useSelector } from 'Hooks';

// When handling dates in QO we use Date type and not DateTime from luxon.
// Reason for this is that react-calendar uses Date type and not DateTime.
// That is why convert toJSDate() is used.

const useDates = () => {
  const companyTimezone = useSelector((state) => state.companyData?.timezone ?? 'Europe/Stockholm');

  // Helper function: This function is used to convert the date to the correct timezone
  const getDateWithCorrectTimezone = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const specificDate = DateTime.fromObject(
      { year: year, month: month, day: day, hour: 0, minute: 0 },
      { zone: companyTimezone },
    );

    return specificDate;
  };

  // Helper function: Set time to accounting start time
  const setStartTime = (date: DateTime) => {
    return date.set({
      hour: 5,
      minute: 30,
      second: 0,
      millisecond: 0,
    });
  };

  // Helper function: Set time to accounting end time
  const setEndTime = (date: DateTime) => {
    return date.plus({ day: 1 }).set({
      hour: 5,
      minute: 29,
      second: 59,
      millisecond: 999,
    });
  };

  // Get accounting start date and time - returns a utc string
  const getAccountingStartDate = (date: Date): string => {
    const correctDate = getDateWithCorrectTimezone(date);
    const startDateAndTime = setStartTime(correctDate).toUTC().toISO();

    return startDateAndTime ?? '';
  };

  // Get accounting end date and time - returns a utc string
  const getAccountingEndDate = (date: Date): string => {
    const correctDate = getDateWithCorrectTimezone(date);
    const endDateAndTime = setEndTime(correctDate).toUTC().toISO();

    return endDateAndTime ?? '';
  };

  // Additional methods to get specific dates and periods
  const getStartDate = (date: Date): Date => {
    const correctDate = getDateWithCorrectTimezone(date);
    const startDate = setStartTime(correctDate).toJSDate();
    return startDate;
  };

  const getEndDate = (date: Date): Date => {
    const correctDate = getDateWithCorrectTimezone(date);
    const endDate = setEndTime(correctDate).toJSDate();
    return endDate;
  };

  const getToday = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const today = setStartTime(correctDate).toUTC();
    return today.toJSDate();
  };

  const getTodayString = (): string => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const today = setStartTime(correctDate).toUTC().toString();
    return today;
  };

  const getStartOfCurrentWeek = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfWeek = setStartTime(correctDate.startOf('week'));
    return startOfWeek.toJSDate();
  };

  const getEndOfCurrentWeek = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfWeek = setEndTime(correctDate.endOf('week'));
    return endOfWeek.toJSDate();
  };

  // N(umber) weeks back
  const getStartOfLastNWeeks = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfWeek = setStartTime(correctDate.startOf('week').minus({ weeks: n }));
    return startOfWeek.toJSDate();
  };

  const getEndOfLastNWeeks = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfWeek = setEndTime(correctDate.minus({ weeks: n }).endOf('week'));
    return endOfWeek.toJSDate();
  };

  const getStartOfCurrentMonth = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfMonth = setStartTime(correctDate.startOf('month'));
    return startOfMonth.toJSDate();
  };

  const getEndOfCurrentMonth = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfMonth = setEndTime(correctDate.endOf('month'));
    return endOfMonth.toJSDate();
  };

  // N(umber) months back
  const getStartOfLastNMonths = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfMonth = setStartTime(correctDate.startOf('month').minus({ months: n }));
    return startOfMonth.toJSDate();
  };

  const getEndOfLastNMonths = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfMonth = setEndTime(correctDate.minus({ months: n }).endOf('month'));
    return endOfMonth.toJSDate();
  };

  const getStartOfCurrentQuarter = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfQuarter = setStartTime(correctDate.startOf('quarter'));
    return startOfQuarter.toJSDate();
  };

  const getEndOfCurrentQuarter = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfQuarter = setEndTime(correctDate.endOf('quarter'));
    return endOfQuarter.toJSDate();
  };

  // N(umber) quarters back
  const getStartOfLastNQuarters = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfQuarter = setStartTime(correctDate.startOf('quarter').minus({ quarters: n }));
    return startOfQuarter.toJSDate();
  };

  const getEndOfLastNQuarters = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfQuarter = setEndTime(correctDate.minus({ quarters: n }).endOf('quarter'));
    return endOfQuarter.toJSDate();
  };

  const getStartOfCurrentYear = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfYear = setStartTime(correctDate.startOf('year'));
    return startOfYear.toJSDate();
  };

  const getEndOfCurrentYear = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfYear = setEndTime(correctDate.endOf('year'));
    return endOfYear.toJSDate();
  };

  // N(umber) years back
  const getStartOfLastNYears = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfYear = setStartTime(correctDate.startOf('year').minus({ years: n }));
    return startOfYear.toJSDate();
  };

  const getEndOfLastNYears = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfYear = setEndTime(correctDate.minus({ years: n }).endOf('year'));
    return endOfYear.toJSDate();
  };

  const getStartOfLastNDays = (n: number, yearsAgo = 0): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date()).minus({
      years: yearsAgo,
    });
    const startOfPeriod = setStartTime(correctDate.minus({ days: n }));
    return startOfPeriod.toJSDate();
  };

  const getEndOfLastNDays = (yearsAgo = 0): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date()).minus({
      years: yearsAgo,
    });
    const endOfPeriod = setEndTime(correctDate);
    return endOfPeriod.toJSDate();
  };

  const getStartOfAllTime = (): Date => {
    // Assuming '2017-04-01' as the foundation date of Baemingo
    const foundationDateJS = new Date('2017-04-01');
    const foundationDate = getDateWithCorrectTimezone(foundationDateJS);
    return setStartTime(foundationDate).toJSDate();
  };

  const isBetweenMidnightAnd530 = (date: DateTime): boolean => {
    return date.hour >= 0 && (date.hour < 5 || (date.hour === 5 && date.minute < 30));
  };

  const getUTCStartOfDay = (year: number, month: number, day: number): DateTime => {
    const date = DateTime.utc(year, month, day);
    return date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  };

  const getUTCEndOfDay = (year: number, month: number, day: number): DateTime => {
    const date = DateTime.utc(year, month, day);
    return date.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
  };

  //TODO: This needs updating
  const formatDatesToPeriod = (startDate: string, endDate: string): string => {
    const correctStartDate = DateTime.fromISO(startDate, { zone: 'utc' });

    // Subtract 1 day from the end date to get the correct end date for display purposes. The real end date is actually the date after until 5:29:59.999
    const correctEndDate = DateTime.fromISO(endDate, { zone: 'utc' }).minus({ day: 1 });

    const startDay = correctStartDate.day;
    const endDay = correctEndDate.day;

    const startMonth = correctStartDate.month - 1; // Luxon months start at 1
    const endMonth = correctEndDate.month - 1;

    const startYear = correctStartDate.year;
    const endYear = correctEndDate.year;

    const startWeekNumber = correctStartDate.weekNumber;
    const endWeekNumber = correctEndDate.weekNumber;

    const startMonthName = correctStartDate.toFormat('MMMM');
    const endMonthName = correctEndDate.toFormat('MMMM');

    const startDayOfWeek = correctStartDate.weekday;
    const endDayOfWeek = correctEndDate.weekday;

    const isStartOfMonth = startDay === 1;
    const isEndOfMonth = endDay === correctEndDate.daysInMonth;

    const isStartOfWeek = startDayOfWeek === 1;
    const isEndOfWeek = endDayOfWeek === 7;

    const isSameDay = correctStartDate.hasSame(correctEndDate, 'day');
    const isSameMonth = correctStartDate.hasSame(correctEndDate, 'month');
    const isSameYear = correctStartDate.hasSame(correctEndDate, 'year');

    const startQuarter = Math.floor(startMonth / 3) + 1;
    const endQuarter = Math.floor(endMonth / 3) + 1;

    const firstMonthIsStartOfQuarter = startMonth % 3 === 0;
    const lastMonthIsEndOfQuarter = endMonth % 3 === 2;

    const isSameQuarter = startQuarter === endQuarter;

    if (isSameYear && isSameMonth && isSameDay) {
      return correctStartDate.toFormat('MMMM d, yyyy');
    }

    if (
      isSameYear &&
      isSameQuarter &&
      firstMonthIsStartOfQuarter &&
      lastMonthIsEndOfQuarter &&
      isStartOfMonth &&
      isEndOfMonth
    ) {
      return `Q${startQuarter} ${startYear}`;
    }

    if (isSameMonth && isSameYear && isStartOfMonth && isEndOfMonth) {
      return `${startMonthName}, ${startYear}`;
    }

    if (isSameMonth && isSameYear && isStartOfWeek && isEndOfWeek) {
      return `Week ${startWeekNumber}, ${startYear}`;
    }

    return `${correctStartDate.toFormat('dd/MM')} - ${correctEndDate.toFormat('dd/MM')}`;
  };

  const formatPastDateToTimeAgo = (date: Date | string): string => {
    const pastDate = DateTime.fromISO(new Date(date).toISOString());
    const now = DateTime.now();

    const diffInSeconds = now.diff(pastDate, 'seconds').seconds;
    const diffInMinutes = now.diff(pastDate, 'minutes').minutes;
    const diffInHours = now.diff(pastDate, 'hours').hours;
    const diffInDays = now.diff(pastDate, 'days').days;
    const diffInMonths = now.diff(pastDate, 'months').months;

    if (diffInSeconds < 60) {
      const seconds = Math.floor(diffInSeconds);
      return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    } else if (diffInMinutes < 60) {
      const minutes = Math.floor(diffInMinutes);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 30) {
      const days = Math.floor(diffInDays);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      const months = Math.floor(diffInMonths);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }
  };

  return {
    getAccountingStartDate,
    getAccountingEndDate,
    getStartDate,
    getEndDate,
    getToday,
    getTodayString,
    getStartOfCurrentWeek,
    getEndOfCurrentWeek,
    getStartOfLastNWeeks,
    getEndOfLastNWeeks,
    getStartOfCurrentMonth,
    getEndOfCurrentMonth,
    getStartOfLastNMonths,
    getEndOfLastNMonths,
    getStartOfCurrentQuarter,
    getEndOfCurrentQuarter,
    getStartOfLastNQuarters,
    getEndOfLastNQuarters,
    getStartOfCurrentYear,
    getEndOfCurrentYear,
    getStartOfLastNYears,
    getEndOfLastNYears,
    getStartOfLastNDays,
    getEndOfLastNDays,
    getStartOfAllTime,
    isBetweenMidnightAnd530,
    getDateWithCorrectTimezone,
    setStartTime,
    formatDatesToPeriod,
    formatPastDateToTimeAgo,
    getUTCStartOfDay,
    getUTCEndOfDay,
  };
};

export default useDates;
